import axios from "axios";
import { extractApiErrorMessage, toNumber } from "./safeData.js";

/**
 * Universal order submission engine.
 *
 * One entry point for both writes that create an order: the customer's cart
 * checkout and the admin's manual concierge order. Both go through the DHC API,
 * never straight to Supabase — `cheep_orders` is service_role-only by policy,
 * and the API is where landed pricing and the profit floor are enforced. A
 * browser-side insert would create an order that skips both and that
 * /admin/orders (backed by Mongo) would never show.
 *
 * @typedef {object} ComprehensiveOrderItem
 * @property {string} productId
 * @property {string} [variantId]
 * @property {string} title
 * @property {number} unitPrice
 * @property {number} quantity
 *
 * @typedef {object} DirectOrderSubmissionPayload
 * @property {string} customerEmail
 * @property {ComprehensiveOrderItem[]} [items]
 * @property {number} [totalAmount]   Overrides the line-item sum.
 * @property {string} [customerName]
 * @property {string} [currency]
 * @property {string} [destination]
 * @property {object} [shippingAddress]
 * @property {string} [notes]
 *
 * @typedef {object} OrderSubmissionResult
 * @property {boolean} success
 * @property {string} [orderId]
 * @property {string} [checkoutUrl]
 * @property {number} [totalAmount]
 * @property {string} [status]
 * @property {string} [error]
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeOrderItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      product_id: item?.productId ?? item?.product_id ?? null,
      variant_id: item?.variantId ?? item?.variant_id ?? null,
      name: (item?.title ?? item?.name ?? "Manual line item").toString().trim() || "Manual line item",
      unit_price: toNumber(item?.unitPrice ?? item?.unit_price, 0),
      quantity: Math.max(1, Math.trunc(toNumber(item?.quantity, 1) || 1)),
    }))
    .filter((item) => item.unit_price >= 0);
}

const roundToCents = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Rounds each line to cents before summing, matching how the API totals a
 * manual order — otherwise the amount shown to the admin can differ by a cent
 * from the amount stored on the order.
 */
export function calculateItemsTotal(items) {
  const total = normalizeOrderItems(items).reduce(
    (sum, item) => sum + roundToCents(item.unit_price * item.quantity),
    0
  );
  return roundToCents(total);
}

/**
 * Validate a submission before it reaches the network.
 * @returns {string} An empty string when the payload is submittable.
 */
export function validateOrderSubmission(payload) {
  const email = (payload?.customerEmail ?? "").trim();
  if (!email) return "Customer email is required";
  if (!EMAIL_PATTERN.test(email)) return "Enter a valid customer email address";

  const items = normalizeOrderItems(payload?.items);
  const explicitTotal = toNumber(payload?.totalAmount);
  if (items.length === 0 && explicitTotal === null) {
    return "Add at least one line item or an order amount";
  }

  const total = explicitTotal === null ? calculateItemsTotal(items) : explicitTotal;
  if (total <= 0) return "Order amount must be greater than zero";

  return "";
}

/**
 * Create an order and return a checkout link the customer can pay through.
 *
 * @param {DirectOrderSubmissionPayload} payload
 * @param {{ apiBase: string, origin?: string }} context
 * @returns {Promise<OrderSubmissionResult>}
 */
export async function executeCompleteOrderSubmission(payload, context) {
  const validationError = validateOrderSubmission(payload);
  if (validationError) {
    return { success: false, error: validationError };
  }

  const items = normalizeOrderItems(payload.items);
  const explicitTotal = toNumber(payload.totalAmount);
  const totalAmount = explicitTotal === null ? calculateItemsTotal(items) : explicitTotal;

  try {
    const response = await axios.post(
      `${context.apiBase}/admin/orders/manual`,
      {
        customer_email: payload.customerEmail.trim().toLowerCase(),
        customer_name: payload.customerName?.trim() || null,
        total_amount: totalAmount,
        currency: payload.currency || "USD",
        destination: payload.destination || "BB",
        shipping_address: payload.shippingAddress || null,
        notes: payload.notes?.trim() || null,
        items: items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      },
      { withCredentials: true }
    );

    const orderId = response.data?.order_id;
    if (!orderId) {
      return { success: false, error: "The order was not created — no order id was returned" };
    }

    // The API builds the link against the storefront origin it was called from;
    // fall back to this window only if that is somehow missing.
    const checkoutUrl =
      response.data?.checkout_url ||
      `${(context.origin || "").replace(/\/+$/, "")}/checkout?order_id=${encodeURIComponent(orderId)}`;

    return {
      success: true,
      orderId,
      checkoutUrl,
      totalAmount: toNumber(response.data?.total, totalAmount),
      status: response.data?.status || "pending_payment",
    };
  } catch (err) {
    console.error("Order Submission Engine Error:", err);
    return {
      success: false,
      error: extractApiErrorMessage(err, "An error occurred processing the order submission."),
    };
  }
}
